import { Platform } from 'react-native';
import NetInfo from '@react-native-community/netinfo';
import { getCacheService, getOfflineDatabase } from './platformServices';
import { reviewService } from './reviewService';
import { api } from './api';
import { filterReviewsForCaching } from '../utils/reviewFilters';
import { FULL_REVIEW_TEMPLATES_PATH } from './templateApiPaths';

// Dependencies interface for testing
export interface LoginCacheStrategyDeps {
  cacheService?: any;
  offlineDatabase?: any;
  apiService?: any;
  reviewService?: any;
}

const cacheService = getCacheService();
const offlineDatabase = getOfflineDatabase();

/**
 * Login cache strategy for offline functionality.
 * Caches the data needed after login for complete offline operation.
 */
class LoginCacheStrategy {
  private isOnline: boolean = true;
  private cacheService: any;
  private offlineDatabase: any;
  private apiService: any;
  private reviewServiceInstance: any;

  constructor(deps?: LoginCacheStrategyDeps) {
    this.cacheService = deps?.cacheService || cacheService;
    this.offlineDatabase = deps?.offlineDatabase || offlineDatabase;
    this.apiService = deps?.apiService || api;
    this.reviewServiceInstance = deps?.reviewService || reviewService;

    if (Platform.OS !== 'web') {
      this.initializeNetworkListener();
    }
  }

  private initializeNetworkListener(): void {
    NetInfo.addEventListener((state) => {
      this.isOnline = state.isConnected ?? false;
    });
  }

  /**
   * Cache all data needed for offline operation after login
   * This should be called immediately after successful authentication
   */
  async cacheAllDataForOffline(): Promise<void> {
    if (Platform.OS === 'web') {
      return;
    }

    const errors: string[] = [];
    let reviewsToCache: any[] = [];

    try {
      // Initialize services
      try {
        if (this.cacheService && this.cacheService.initialize) {
          await this.cacheService.initialize();
        }
      } catch (error) {
        const message = `Cache service init: ${error.message}`;
        errors.push(message);
        console.error('[LoginCacheStrategy]', message);
      }

      try {
        if (this.offlineDatabase && this.offlineDatabase.initialize) {
          await this.offlineDatabase.initialize();
        }
      } catch (error) {
        const message = `Database init: ${error.message}`;
        errors.push(message);
        console.error('[LoginCacheStrategy]', message);
        return;
      }

      // Cache all templates with categories and items
      try {
        let templates = await this.apiService.get(FULL_REVIEW_TEMPLATES_PATH);

        // ALWAYS fetch full template details to get sharingLinks for offline pre-fill
        // Use batching to avoid server spike
        const BATCH_SIZE = 3; // Fetch 3 templates at a time
        const templateBatches: any[][] = [];

        for (let i = 0; i < templates.length; i += BATCH_SIZE) {
          templateBatches.push(templates.slice(i, i + BATCH_SIZE));
        }

        for (const batch of templateBatches) {
          // Fetch batch in parallel
          const detailPromises = batch.map(async (template, batchIndex) => {
            try {
              const fullTemplate = await this.apiService.get(`/reviews/templates/${template.id}`);
              return { success: true, template: fullTemplate, originalIndex: batchIndex };
            } catch (error) {
              console.error(`[LoginCacheStrategy] Failed to fetch template ${template.id}:`, error);
              return { success: false, template: template, originalIndex: batchIndex, error };
            }
          });

          const results = await Promise.allSettled(detailPromises);

          // Process results
          results.forEach((result, idx) => {
            if (result.status === 'fulfilled' && result.value.success) {
              const globalIndex = templateBatches.indexOf(batch) * BATCH_SIZE + idx;
              templates[globalIndex] = result.value.template;
            }
          });

          // Small delay between batches to avoid overwhelming server
          if (templateBatches.indexOf(batch) < templateBatches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }

        if (this.offlineDatabase && this.offlineDatabase.cacheTemplates) {
          await this.offlineDatabase.cacheTemplates(templates, 168); // Cache for 7 days

          // Clean up very old (stale) templates to prevent unbounded SQLite growth
          // This removes templates expired more than 30 days ago
          if (this.offlineDatabase.clearStaleTemplates) {
            await this.offlineDatabase.clearStaleTemplates(720); // 30 days
          }

          // Cache sharing links for offline prefill support
          try {
            const allSharingLinks: any[] = [];
            for (const template of templates) {
              if (template.sharingLinks && template.sharingLinks.length > 0) {
                allSharingLinks.push(...template.sharingLinks);
              }
            }

            if (allSharingLinks.length > 0) {
              await this.offlineDatabase.cacheSharedItemLinks(allSharingLinks);
              await this.offlineDatabase.cacheQuestionMappings(allSharingLinks);
            }
          } catch (sharingError) {
            console.warn('[LoginCacheStrategy] Error caching sharing links:', sharingError);
            // Don't add to errors - this is non-critical for basic functionality
          }
        }
      } catch (error) {
        errors.push(`Templates: ${error.message}`);
      }

      // Cache all rooms (default locations)
      try {
        const rooms = await this.apiService.get('/rooms');
        if (this.offlineDatabase && this.offlineDatabase.cacheRooms) {
          await this.offlineDatabase.cacheRooms(rooms, undefined, 168); // Cache for 7 days
        }
      } catch (error) {
        errors.push(`Rooms: ${error.message}`);
      }

      // Cache all user's reviews with full details
      try {
        const reviews = await this.apiService.get('/reviews');

        // Filter to most recent 10 draft/in-progress reviews for caching
        reviewsToCache = filterReviewsForCaching(reviews);

        // Prune stale cached reviews that are no longer in the active set
        try {
          const existingReviews = await this.offlineDatabase.getReviews();
          const newReviewIds = new Set(reviewsToCache.map(r => r.id));

          for (const existing of existingReviews) {
            // Only delete synced reviews not in new set (preserve pending offline work)
            if (!newReviewIds.has(existing.id) && existing.syncStatus === 'synced') {
              const recoverableWorkCount = this.offlineDatabase.getRecoverableWorkCount
                ? await this.offlineDatabase.getRecoverableWorkCount(existing.id).catch(() => 0)
                : 0;

              if (recoverableWorkCount > 0) {
                await this.offlineDatabase.createRecoverySnapshot?.(existing.id, 'login_cache_prune_guard');
                console.warn('[LoginCacheStrategy] Preserving cached review with recoverable offline work during prune:', {
                  reviewId: existing.id,
                  recoverableWorkCount,
                });
                continue;
              }

              await this.offlineDatabase.deleteReview(existing.id);

              // Delete associated synced on-demand items
              const onDemandItems = await this.offlineDatabase.getOfflineOnDemandItems(existing.id).catch(() => []);
              for (const item of onDemandItems) {
                if (item.sync_status === 'synced') {
                  await this.offlineDatabase.deleteOfflineOnDemandItem(item.id);
                }
              }
            }
          }
        } catch (pruneError) {
          console.error('[LoginCacheStrategy] ❌ Error pruning old reviews:', pruneError);
          // Don't throw - continue with caching
        }

        for (const review of reviewsToCache) {
          try {
            // Get full review details including responses
            const fullReview = await this.apiService.get(`/reviews/${review.id}`);

            // If the review comes with a fully populated template, cache it
            if (fullReview.template && fullReview.template.categories) {
              // Check if this template has items (API returns full template with review)
              const hasItems = fullReview.template.categories.some(cat => cat.items && cat.items.length > 0);
              if (hasItems) {
                // Update the cached template with the full version from the review
                // This ensures we have items (including custom items) in the cached template
                if (this.offlineDatabase && this.offlineDatabase.cacheTemplates) {
                  await this.offlineDatabase.cacheTemplates([fullReview.template], 168);
                }
              }
            }

            // Cache the review
            if (this.offlineDatabase && this.offlineDatabase.createReviewWithId) {
              // Check if review already exists
              const existingReview = await this.offlineDatabase.getReview(review.id).catch(() => null);

              if (!existingReview) {
                const reviewData = {
                  id: fullReview.id,
                  propertyId: fullReview.propertyId || fullReview.property?.id,
                  templateId: fullReview.templateId,
                  reviewerId: fullReview.reviewerId,
                  status: fullReview.status,
                  startedAt: fullReview.startedAt,
                  completedAt: fullReview.completedAt,
                  submittedAt: fullReview.submittedAt,
                  notes: fullReview.notes,
                  address: fullReview.property?.address || fullReview.address,
                  syncStatus: 'synced',
                };
                await this.offlineDatabase.createReviewWithId(reviewData);
              }

              // Cache all responses for this review
              if (fullReview.responses && fullReview.responses.length > 0) {
                for (const response of fullReview.responses) {
                  const existingResponse = await this.offlineDatabase.getResponse(response.id).catch(() => null);

                  // Don't overwrite pending or failed responses - they need to sync first
                  if (existingResponse && (existingResponse.syncStatus === 'pending' || existingResponse.syncStatus === 'failed')) {
                    continue;
                  }

                  if (!existingResponse) {
                    // Extract full photo metadata for proper caching
                    // This enables offline review creation to copy photos from shared items
                    const photoMetadata = (response.photos || []).map((photo: any) => ({
                      id: photo.id,
                      responseId: response.id,
                      s3Key: photo.s3Key,
                      s3Url: photo.s3Url,
                      fileName: photo.fileName,
                      mimeType: photo.mimeType,
                      size: photo.size,
                      caption: photo.caption,
                      metadata: photo.metadata ? JSON.stringify(photo.metadata) : undefined,
                      questionId: photo.metadata?.questionId,
                      uploadedAt: photo.uploadedAt,
                    }));

                    const responseData = {
                      id: response.id,
                      reviewId: fullReview.id,
                      itemId: response.itemId,
                      roomId: response.roomId,
                      isPresent: response.isPresent,
                      status: response.status,
                      responseData: response.responseData || {},
                      notes: response.notes,
                      photos: photoMetadata,
                      syncStatus: 'synced',
                    };
                    await this.offlineDatabase.createResponseWithId(responseData);
                  }
                }
              }

              // Cache property-specific rooms if available
              if (fullReview.property?.rooms) {
                await this.offlineDatabase.cacheRooms(
                  fullReview.property.rooms,
                  fullReview.propertyId || fullReview.property?.id,
                  168
                );
              }

              // Cache on-demand items for this review
              try {
                const onDemandItems = await this.apiService.get(`/reviews/${fullReview.id}/on-demand-items`);

                if (onDemandItems && onDemandItems.length > 0) {
                  // Cache each on-demand item
                  if (this.offlineDatabase.createOfflineOnDemandItem) {
                    for (const item of onDemandItems) {
                      try {
                        const existingItem = await this.offlineDatabase.getOfflineOnDemandItem(item.id).catch(() => null);

                        if (!existingItem) {
                          await this.offlineDatabase.createOfflineOnDemandItem({
                            id: item.id,
                            reviewId: fullReview.id,
                            name: item.name,
                            description: item.description,
                            categoryId: item.categoryId,
                            questionsSchema: item.questionsSchema,
                            createdAt: item.createdAt,
                            syncStatus: 'synced',
                          });
                        }
                      } catch (itemError) {
                        // Continue with next item
                      }
                    }
                  }
                }
              } catch (onDemandError) {
                // Silently continue if on-demand items fail to load
              }
            }

          } catch (reviewError) {
            errors.push(`Review ${review.id}: ${reviewError.message}`);
          }
        }

        // Extract unique property IDs from filtered reviews and prefetch their rooms
        const propertyIds = new Set<number>();
        reviewsToCache.forEach(review => {
          if (review.propertyId) {
            propertyIds.add(review.propertyId);
          }
        });

        if (propertyIds.size > 0) {
          // Prefetch rooms for each property while still online
          for (const propertyId of propertyIds) {
            try {
              await this.cacheService.refreshRooms(propertyId);
            } catch (err) {
              console.error('[LoginCacheStrategy] ❌ Failed to cache rooms for property', propertyId, err);
              // Don't throw - continue with other properties
            }
          }
        }
      } catch (error) {
        console.error('[LoginCacheStrategy] Error caching reviews:', error);
        errors.push(`Reviews: ${error.message}`);
      }

      // Update cache metadata
      if (this.offlineDatabase && this.offlineDatabase.updateCacheMetadata) {
        await this.offlineDatabase.updateCacheMetadata('comprehensive', new Date());
      }

      // Seed historical snapshots so offline view is accurate
      if (this.reviewServiceInstance && Array.isArray(reviewsToCache) && reviewsToCache.length > 0) {
        try {
          await this.reviewServiceInstance.preloadReviewSnapshots(reviewsToCache);
        } catch (snapshotError) {
          const message = `Snapshot preload: ${snapshotError.message}`;
          errors.push(message);
          console.error('[LoginCacheStrategy]', message);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if the login cache is valid.
   */
  async isCacheValid(): Promise<boolean> {
    if (Platform.OS === 'web') {
      return false;
    }

    if (!this.offlineDatabase || !this.offlineDatabase.getCacheMetadata) {
      return false;
    }

    try {
      const metadata = await this.offlineDatabase.getCacheMetadata('comprehensive');
      if (!metadata) {
        return false;
      }

      // Check if cache is less than 7 days old
      const cacheAge = Date.now() - new Date(metadata.lastUpdated).getTime();
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

      return cacheAge < sevenDaysInMs;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clear all cached data
   */
  async clearAllCaches(): Promise<void> {
    if (Platform.OS === 'web') {
      return;
    }

    try {
      if (this.cacheService && this.cacheService.clearAllCaches) {
        await this.cacheService.clearAllCaches();
      }
      if (this.offlineDatabase && this.offlineDatabase.clearAll) {
        await this.offlineDatabase.clearAll();
      }
    } catch (error) {
      // Error clearing caches - silently fail
    }
  }
}

// Factory function for creating testable instances
export function createLoginCacheStrategy(deps?: LoginCacheStrategyDeps): LoginCacheStrategy {
  return new LoginCacheStrategy(deps);
}

// Default singleton instance for app use
export const loginCacheStrategy = new LoginCacheStrategy();
